/**
 * Offline Banner
 *
 * Shows when the browser is offline or IndexedDB has pending commands.
 * (Legacy /api/local/* status+sync endpoints are not mounted — do not call them.)
 */

import { useState, useEffect, useCallback } from 'react';
import { isOnline, onOnlineStatusChange } from '../utils/pwa';
import { getPendingCommands, type PendingCommand } from '../utils/storage';

export function OfflineBanner() {
  const [online, setOnline] = useState(isOnline());
  const [pendingCount, setPendingCount] = useState(0);
  const [showDetails, setShowDetails] = useState(false);

  const refreshPending = useCallback(async () => {
    try {
      const pending = await getPendingCommands();
      setPendingCount(pending.length);
    } catch {
      // IndexedDB not available
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onOnlineStatusChange((isNowOnline) => {
      setOnline(isNowOnline);
      if (isNowOnline) void refreshPending();
    });

    void refreshPending();
    const interval = setInterval(() => { void refreshPending(); }, 5000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [refreshPending]);

  if (online && pendingCount === 0) {
    return null;
  }

  return (
    <div className={`flex items-center justify-center gap-4 px-4 py-1 text-sm font-medium text-black ${
      online ? 'bg-warning' : 'bg-danger'
    }`}>
      <span className="text-lg">
        {online ? '⚡' : '📴'}
      </span>

      <span>
        {online
          ? `${pendingCount} command${pendingCount === 1 ? '' : 's'} pending sync`
          : 'Offline - Changes will sync when connected'}
      </span>

      {pendingCount > 0 && (
        <span className="rounded-full bg-black/20 px-2 py-0.5">
          {pendingCount} commands
        </span>
      )}

      <button
        onClick={() => setShowDetails(!showDetails)}
        className="bg-transparent border-none cursor-pointer text-inherit p-1 tap:min-h-12 tap:min-w-12"
      >
        {showDetails ? '▲' : '▼'}
      </button>

      {showDetails && (
        <SyncDetails
          pendingCount={pendingCount}
          onClose={() => setShowDetails(false)}
        />
      )}
    </div>
  );
}

function SyncDetails({
  pendingCount,
  onClose,
}: {
  pendingCount: number;
  onClose: () => void;
}) {
  const [commands, setCommands] = useState<PendingCommand[]>([]);

  useEffect(() => {
    getPendingCommands().then(setCommands).catch(() => {});
  }, []);

  return (
    <div className="fixed top-[40px] right-4 bg-surface backdrop-blur-xl border border-border rounded-lg p-4 w-[min(320px,calc(100vw-2rem))] max-h-[450px] overflow-auto z-[100] shadow-glass-elevated">
      <div className="flex justify-between items-center mb-4 pb-2 border-b border-border">
        <span className="font-semibold text-text text-base">
          Sync Status
        </span>
        <button
          onClick={onClose}
          className="bg-none border-none text-text-muted cursor-pointer text-lg tap:min-h-12"
        >
          ×
        </button>
      </div>

      <div className="grid grid-cols-1 gap-2 mb-4">
        <StatBox label="Pending Commands" value={pendingCount} color="warning" />
      </div>

      <div className="mb-2">
        <div className="text-xs text-text-muted font-semibold mb-1">
          PENDING COMMANDS
        </div>

        {commands.length === 0 ? (
          <div className="text-text-dim text-sm p-2 text-center">
            No pending commands
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {commands.slice(0, 10).map((cmd, i) => (
              <div
                key={i}
                className="p-2 bg-surface-active rounded text-xs"
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium text-text">
                    {cmd.type}
                  </span>
                  <span className="text-text-dim text-xs">
                    {new Date(cmd.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="text-text-muted mt-0.5">
                  Target: {cmd.targetId}
                </div>
              </div>
            ))}
            {commands.length > 10 && (
              <div className="text-center text-text-muted text-xs p-1">
                +{commands.length - 10} more
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, color }: { label: string; value: number; color: 'warning' | 'info' }) {
  return (
    <div className={`p-2 rounded text-center ${
      value > 0 ? `bg-${color}/20` : 'bg-surface-active'
    }`}>
      <div className={`text-xl font-bold ${
        value > 0 ? `text-${color}` : 'text-text-muted'
      }`}>
        {value}
      </div>
      <div className="text-xs text-text-muted">
        {label}
      </div>
    </div>
  );
}

export default OfflineBanner;
